import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";

/**
 * Live camera scanner — reads 1D barcodes (EAN/UPC/Code128/ITF) AND QR codes.
 *
 * Props:
 *   onDetected(code: string)   — called once with the first stable read.
 *   onClose()                  — user closed the modal.
 */
export default function BarcodeCameraModal({ onDetected, onClose }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const onDetectedRef = useRef(onDetected);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(true);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualValue, setManualValue] = useState("");

  useEffect(() => { onDetectedRef.current = onDetected; }, [onDetected]);

  useEffect(() => {
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.CODE_128,
      BarcodeFormat.ITF,
      BarcodeFormat.QR_CODE,
    ]);
    hints.set(DecodeHintType.TRY_HARDER, true);
    const reader = new BrowserMultiFormatReader(hints);
    readerRef.current = reader;

    let cancelled = false;

    (async () => {
      try {
        // Probe for camera permission with a rear-camera hint. iOS Safari
        // sometimes refuses to attach without an explicit prior request.
        try {
          const probe = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: "environment" } },
            audio: false,
          });
          probe.getTracks().forEach((t) => t.stop());
        } catch (permErr) {
          if (/Permission|NotAllowed/i.test(permErr?.message || "") || permErr?.name === "NotAllowedError") {
            if (!cancelled) {
              setError("Camera blocked. In your browser, allow camera access for this site and try again.");
              setStarting(false);
            }
            return;
          }
          // Older browsers may fail the probe; let decodeFromConstraints try.
        }

        // decodeFromConstraints owns the whole stream lifecycle so we don't
        // double-stop and trigger "Track is in an invalid state" errors.
        const controls = await reader.decodeFromConstraints(
          {
            video: {
              facingMode: { ideal: "environment" },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
            audio: false,
          },
          videoRef.current,
          (result, _err, ctrls) => {
            if (cancelled) return;
            if (result) {
              const code = result.getText();
              if (code) {
                cancelled = true;
                try { ctrls.stop(); } catch { /* noop */ }
                onDetectedRef.current?.(code);
              }
            }
          },
        );
        controlsRef.current = controls;

        // Once the stream is live we can inspect the active track for
        // torch / autofocus capabilities. Read-only; we never stop the track
        // ourselves — zxing owns it.
        const srcObj = videoRef.current?.srcObject;
        const track = srcObj && typeof srcObj.getVideoTracks === "function" ? srcObj.getVideoTracks()[0] : null;
        if (track && typeof track.getCapabilities === "function") {
          try {
            const caps = track.getCapabilities();
            if (caps && "torch" in caps) setTorchSupported(true);
            if (caps && "focusMode" in caps && Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous")) {
              try { await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }); } catch { /* ignore */ }
            }
          } catch { /* ignore */ }
        }

        if (!cancelled) setStarting(false);
      } catch (e) {
        if (cancelled) return;
        const msg = e?.message || String(e);
        if (/Permission|NotAllowed/i.test(msg)) {
          setError("Camera blocked. In your browser, allow camera access for this site and try again.");
        } else if (/NotFound|no device/i.test(msg)) {
          setError("No camera found. Try typing or pasting the code instead.");
        } else {
          setError(msg);
        }
        setStarting(false);
      }
    })();

    return () => {
      cancelled = true;
      try { controlsRef.current?.stop(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getActiveTrack = () => {
    const srcObj = videoRef.current?.srcObject;
    if (!srcObj || typeof srcObj.getVideoTracks !== "function") return null;
    const t = srcObj.getVideoTracks()[0];
    if (!t || t.readyState !== "live") return null;
    return t;
  };

  const toggleTorch = async () => {
    const t = getActiveTrack();
    if (!t) return;
    try {
      await t.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn((v) => !v);
    } catch {
      setTorchSupported(false);
    }
  };

  const tapFocus = async () => {
    const t = getActiveTrack();
    if (!t) return;
    try { await t.applyConstraints({ advanced: [{ focusMode: "single-shot" }] }); } catch { /* ignore */ }
    try { await t.applyConstraints({ advanced: [{ focusMode: "continuous" }] }); } catch { /* ignore */ }
  };

  const submitManual = () => {
    const v = manualValue.trim();
    if (!v) return;
    onDetectedRef.current?.(v);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4"
      data-testid="barcode-camera-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-black rounded-3xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aspect-[3/4] w-full bg-black relative" onClick={tapFocus}>
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
            autoPlay
            data-testid="barcode-camera-video"
          />
          {!error && (
            <>
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="relative w-[72%] aspect-square border-2 border-white/70 rounded-2xl overflow-hidden">
                  <div className="absolute left-0 right-0 h-0.5 bg-[#8CFF7D] shadow-[0_0_12px_#8CFF7D] animate-pulse top-1/2" />
                  <span className="absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-white" />
                  <span className="absolute top-0 right-0 w-5 h-5 border-t-2 border-r-2 border-white" />
                  <span className="absolute bottom-0 left-0 w-5 h-5 border-b-2 border-l-2 border-white" />
                  <span className="absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-white" />
                </div>
              </div>
              {torchSupported && (
                <button
                  type="button"
                  onClick={toggleTorch}
                  data-testid="barcode-torch-btn"
                  className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center text-xl transition ${torchOn ? "bg-[#F2C94C] text-black" : "bg-white/20 text-white hover:bg-white/30"}`}
                  aria-label={torchOn ? "Turn torch off" : "Turn torch on"}
                >
                  💡
                </button>
              )}
            </>
          )}
        </div>

        <div className="p-4 text-white text-center">
          {error ? (
            <div className="text-sm text-[#FBC9C0]" data-testid="barcode-camera-error">{error}</div>
          ) : starting ? (
            <div className="text-sm text-white/80">Starting camera…</div>
          ) : (
            <div className="text-sm text-white/80">
              Point the barcode <span className="text-white">or QR code</span> inside the frame
              <div className="text-[11px] text-white/50 mt-1">Tap the preview to refocus{torchSupported ? " · 💡 for low light" : ""}</div>
            </div>
          )}
          <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={() => setManualOpen((v) => !v)}
              className="rounded-full bg-white/15 hover:bg-white/25 transition text-sm font-medium px-4 py-2"
              data-testid="barcode-manual-toggle"
            >
              {manualOpen ? "Hide manual entry" : "Can't scan? Paste link"}
            </button>
            <button
              onClick={onClose}
              className="rounded-full bg-white/15 hover:bg-white/25 transition text-sm font-medium px-5 py-2"
              data-testid="barcode-camera-close"
            >
              Close
            </button>
          </div>
          {manualOpen && (
            <div className="mt-3 flex gap-2" data-testid="barcode-manual-row">
              <input
                autoFocus
                value={manualValue}
                onChange={(e) => setManualValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitManual()}
                placeholder="Paste a barcode or recipe URL"
                className="flex-1 rounded-full px-4 py-2 text-sm text-[#2A332C] bg-white placeholder-[#8B9690]"
                data-testid="barcode-manual-input"
              />
              <button
                onClick={submitManual}
                disabled={!manualValue.trim()}
                className="rounded-full bg-[#3B5A3E] text-white text-sm px-4 py-2 disabled:opacity-50"
                data-testid="barcode-manual-submit"
              >
                Use
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
